import { property } from '@wonderlandengine/api/decorators.js';
import { Configuration, AccentOptions, PaintTypes } from '../data/configuration.js';
import { CloudData } from '../data/cloud-data.js';
import { Component, Material, MeshComponent, NumberArray, Object3D } from '@wonderlandengine/api';
import { PaintDescription } from './components/paint-description.js';
import { Easing, Tween } from '@tweenjs/tween.js';
import { TweenManager } from '../utilities/tween-manager.js';

//-----------------------------------------------------------------------------------------
// Type Definitions:
//-----------------------------------------------------------------------------------------

export interface CarPbrMaterial extends Material {
	albedoColor: NumberArray;
	emissiveColor: NumberArray;
	clearCoatColorF0: NumberArray;
	flakesColor: NumberArray;
	fresnelColor: NumberArray;
	metallicFactor: number;
	smoothnessFactor: number;
	occlusionFactor: number;
	flakesColorStrength: number;
	flakesScale: number;
	flakeStrength: number;
	clearCoatSmoothness: number;
	fresnelPower: number;
}

//-----------------------------------------------------------------------------------------
// Constants:
//-----------------------------------------------------------------------------------------

const GLOSS_SMOOTHNESS_VALUE = 0.9;
const GLOSS_REFLECTIVE_SPECULAR_VALUE = 0.96;
const COLOUR_TWEEN_TIME_IN_MS = 250;

// the paint colour whose material is used as the neutral base for a bespoke colour of each paint type, before its albedo is overridden.
// the base material is what supplies the finish (metallic factor, flakes, clear coat, fresnel), so each one is a light, neutral colour of its own type.
// only the types that offer the bespoke option appear here - solitallic is fixed-colour - so this must be kept in step with
// BESPOKE_PAINT_OPTION_IDS in paintwork-view.tsx, which is what decides that. note the note under 'material assumption' in the porting
// doc before adding a layered finish: on those, a white base washes the chosen colour out and a mid-grey is wanted instead.
const BESPOKE_BASE_PAINT_IDS: Partial<Record<PaintTypes, string>> = {
	[PaintTypes.Solid]: 'PS014',															// gloss solid classic white.
	[PaintTypes.Metallic]: 'PM002'															// metallic silver.
};

/**
 * Paint Configurator:
 */
export class PaintConfigurator extends Component {
	static TypeName = 'paint-configurator';

	//-----------------------------------------------------------------------------------------
	// Inspector Variables:
	//-----------------------------------------------------------------------------------------

	@property.object({ required: true })
	paintDescriptions!: Object3D;

	@property.object({ required: true })
	bonnet!: Object3D;

	@property.object({ required: true })
	bodyRear!: Object3D;

	@property.object({ required: true })
	wings!: Object3D;

	@property.object({ required: true })
	windowSides!: Object3D;

	@property.object({ required: true })
	spareWheelCover!: Object3D;

	@property.object({ required: true })
	spareWheelLamp!: Object3D;

	@property.object({ required: true })
	centerConsole!: Object3D;

	@property.object({ required: true })
	seatBackCarPaint!: Object3D;

	//-----------------------------------------------------------------------------------------
	// Fields:
	//-----------------------------------------------------------------------------------------

	// component references.
	private _paintDescriptions!: PaintDescription[];
	private _bonnet!: MeshComponent;
	private _bodyRear!: MeshComponent;
	private _wings!: MeshComponent;
	private _windowSides!: MeshComponent;
	private _spareWheelCover!: MeshComponent;
	private _spareWheelLamp!: MeshComponent;
	private _centerConsole!: MeshComponent;
	private _seatBackCarPaint!: MeshComponent;

	private _tweenManager = new TweenManager();

	// the per-mesh materials used while a bespoke body colour is applied, cached so that dragging the picker is a cheap albedo write rather than a fresh clone/tween each time. null when no bespoke colour is active.
	private _bespokeBodyMaterials: CarPbrMaterial[] | null = null;

	// the paint type the cached bespoke body materials were cloned from, so that re-basing the colour onto another type re-clones from that type's base material. null when no bespoke colour is active.
	private _bespokeBodyPaintType: PaintTypes | null = null;

	// the meshes those materials were cloned onto. the body mesh set is not fixed - the window sides leave it when the accent pack
	// is selected and rejoin it when it is not - and a mesh that joins after the clones were made would otherwise be left out of the
	// albedo writes, frozen on the colour it had when it joined while the rest of the body followed the picker.
	private _bespokeBodyMeshes: MeshComponent[] | null = null;

	// the same pair for the wings, which are painted separately and may carry a bespoke colour of their own - or the body's, when the two are matching.
	private _bespokeWingMaterial: CarPbrMaterial | null = null;
	private _bespokeWingPaintType: PaintTypes | null = null;

	//-----------------------------------------------------------------------------------------
	// Lifecycle Functions:
	//-----------------------------------------------------------------------------------------

	start(): void {

		// get components.
		this._paintDescriptions = this.paintDescriptions.getComponents(PaintDescription)!;
		this._bonnet = this.bonnet.getComponent(MeshComponent)!;
		this._bodyRear = this.bodyRear.getComponent(MeshComponent)!;
		this._wings = this.wings.getComponent(MeshComponent)!;
		this._windowSides = this.windowSides.getComponent(MeshComponent)!;
		this._spareWheelCover = this.spareWheelCover.getComponent(MeshComponent)!;
		this._spareWheelLamp = this.spareWheelLamp.getComponent(MeshComponent)!;
		this._centerConsole = this.centerConsole.getComponent(MeshComponent)!;
		this._seatBackCarPaint = this.seatBackCarPaint.getComponent(MeshComponent)!;
	}

	update() {
		this._tweenManager.update();
	}

	//-----------------------------------------------------------------------------------------
	// Public Functions:
	//-----------------------------------------------------------------------------------------

	/**
	 * Selects the body paint colour with the given ID, updating the relevant meshes/materials on the vehicle model.
	 */
	selectBodyPaint(id: string) {

		// a bespoke option id stands for 'a colour of the customer's own', not for a paint description, so there is nothing to look up
		// and any colour already applied should stay. bespoke colours arrive via selectBespokeBodyPaint() instead.
		if (Configuration.isBespokePaintColour(id)) {
			return;
		}

		// selecting a standard paint clears any active bespoke colour, so re-opening the picker starts from the base material again.
		this._bespokeBodyMaterials = null;
		this._bespokeBodyPaintType = null;
		this._bespokeBodyMeshes = null;

		const material = this._getMaterialFromId(id);
		if (material) {

			// fade each body mesh to the selected paint material.
			for (const mesh of this._getBodyMeshes()) {
				this._fadeColour(mesh, material);
			}
		}
	}

	/**
	 * Selects the wing paint colour with the given ID, updating the relevant meshes/materials on the vehicle model.
	 */
	selectWingPaint(id: string) {

		// as for the body, a bespoke option id has no paint description - it arrives via selectBespokeWingPaint() instead.
		if (Configuration.isBespokePaintColour(id)) {
			return;
		}

		// selecting a standard paint clears any bespoke colour on the wings.
		this._bespokeWingMaterial = null;
		this._bespokeWingPaintType = null;

		const material = this._getMaterialFromId(id);
		if (material) {
			this._fadeColour(this._wings, material);
		}
	}

	/**
	 * Applies a bespoke (arbitrary) body colour, given as an rgba array of 0–1 floats, updating the vehicle model in real time.
	 * The colour is applied over the neutral base material of the given paint type, so that the finish (solid, metallic or solitallic)
	 * matches the panel the colour was chosen in. The first call for a paint type clones that base material onto each body mesh; subsequent
	 * calls just overwrite the albedo, so dragging the picker stays cheap.
	 */
	selectBespokeBodyPaint(colour: NumberArray, paintType: PaintTypes) {

		// on the first application, whenever the colour is re-based onto another paint type, and whenever the body mesh set itself has
		// changed, clone that type's base material onto each body mesh and cache the clones.
		const bodyMeshes = this._getBodyMeshes();
		if (!this._bespokeBodyMaterials || this._bespokeBodyPaintType !== paintType || !isSameMeshSet(this._bespokeBodyMeshes, bodyMeshes)) {

			const basePaintId = getBespokeBasePaintId(paintType);
			const baseMaterial = basePaintId ? this._getMaterialFromId(basePaintId) : null;
			if (!baseMaterial) {
				return;
			}

			this._bespokeBodyMaterials = [];
			for (const mesh of bodyMeshes) {
				const material = baseMaterial.clone() as CarPbrMaterial;
				mesh.material = material;
				this._bespokeBodyMaterials.push(material);
			}

			// remember the type and the meshes the clones came from, so a later call matching both reuses them.
			this._bespokeBodyPaintType = paintType;
			this._bespokeBodyMeshes = bodyMeshes;
		}

		// overwrite the albedo of each cached material with the chosen colour.
		for (const material of this._bespokeBodyMaterials) {
			material.albedoColor = [colour[0], colour[1], colour[2], colour[3] ?? 1];
		}
	}

	/**
	 * The wings' twin of selectBespokeBodyPaint(), applying a bespoke colour to the wings alone. Needed both for a wing colour chosen
	 * in its own right and for the matching rule - wings that were wearing the body's colour must follow it to a bespoke one rather
	 * than being left behind on the last standard paint.
	 */
	selectBespokeWingPaint(colour: NumberArray, paintType: PaintTypes) {

		// clone the paint type's base material onto the wings on the first application, or whenever the type changes.
		if (!this._bespokeWingMaterial || this._bespokeWingPaintType !== paintType) {

			const basePaintId = getBespokeBasePaintId(paintType);
			const baseMaterial = basePaintId ? this._getMaterialFromId(basePaintId) : null;
			if (!baseMaterial) {
				return;
			}

			const material = baseMaterial.clone() as CarPbrMaterial;
			this._wings.material = material;
			this._bespokeWingMaterial = material;
			this._bespokeWingPaintType = paintType;
		}

		// overwrite its albedo with the chosen colour.
		this._bespokeWingMaterial.albedoColor = [colour[0], colour[1], colour[2], colour[3] ?? 1];
	}

	//-----------------------------------------------------------------------------------------
	// Private Functions:
	//-----------------------------------------------------------------------------------------
	
	// returns the meshes that make up the painted body - the wings are painted separately, so they are not among them. the window
	// sides join the body only when the accent pack is not selected, which colours them itself.
	private _getBodyMeshes(): MeshComponent[] {

		const meshes = [
			this._bonnet,
			this._bodyRear,
			this._spareWheelCover,
			this._centerConsole,
			this._spareWheelLamp,
			this._seatBackCarPaint
		];

		if (Configuration.accentPackOption !== AccentOptions.AccentPack) {
			meshes.push(this._windowSides);
		}

		return meshes;
	}

	private _getMaterialFromId(id: string): CarPbrMaterial | null {

		// get the paint description for the given ID.
		const paintDescription = this._paintDescriptions.find(element => element.paintId === id);
		if (!paintDescription) {
			console.error('No paint description found for id: ', id);
			return null;
		}

		// get the relevant material from the paint description.
		const material = paintDescription.paintMaterial as CarPbrMaterial;
		if (!material) {
			console.error('No paint material assigned for id: ', id);
			return null;
		}

		// return the material.
		return material;
	}

	/**
	 * Smoothly fades the colour of the material on the given mesh between the current colour and the colour defined by the given material,
	 * using a tween animation to change the relevant material properties.
	 */
	private _fadeColour(mesh: MeshComponent, targetMaterial: CarPbrMaterial) {

		// ensure the given mesh has a material assigned to it.
		if (!mesh.material) {
			console.error('No material has been assigned to the mesh: ', mesh);
			return;
		}

		// clone the current material (as we don't want to change the source material itself).
		const material = mesh.material.clone() as CarPbrMaterial;
		mesh.material = material;

		// set the relevant parameter values.
		material.smoothnessFactor = GLOSS_SMOOTHNESS_VALUE;
		material.clearCoatSmoothness = GLOSS_REFLECTIVE_SPECULAR_VALUE;

		// tween the relevant material property to change the colour.
		const colourTween = new Tween({
			albedoColor: material.albedoColor,
			emissiveColor: material.emissiveColor,
			clearCoatColorF0: material.clearCoatColorF0,
			flakesColor: material.flakesColor,
			fresnelColor: material.fresnelColor,
			metallicFactor: material.metallicFactor,
			occlusionFactor: material.occlusionFactor,
			flakesColorStrength: material.flakesColorStrength,
			flakesScale: material.flakesScale,
			flakeStrength: material.flakeStrength,
			fresnelPower: material.fresnelPower
		});
		colourTween
			.to(
				{
					albedoColor: targetMaterial.albedoColor,
					emissiveColor: targetMaterial.emissiveColor,
					clearCoatColorF0: targetMaterial.clearCoatColorF0,
					flakesColor: targetMaterial.flakesColor,
					fresnelColor: targetMaterial.fresnelColor,
					metallicFactor: targetMaterial.metallicFactor,
					occlusionFactor: targetMaterial.occlusionFactor,
					flakesColorStrength: targetMaterial.flakesColorStrength,
					flakesScale: targetMaterial.flakesScale,
					flakeStrength: targetMaterial.flakeStrength,
					fresnelPower: targetMaterial.fresnelPower
				},
				COLOUR_TWEEN_TIME_IN_MS
			)
			.onUpdate((c) => {
				material.albedoColor = c.albedoColor;
				material.emissiveColor = c.emissiveColor;
				material.clearCoatColorF0 = c.clearCoatColorF0;
				material.flakesColor = c.flakesColor;
				material.fresnelColor = c.fresnelColor;
				material.metallicFactor = c.metallicFactor;
				material.occlusionFactor = c.occlusionFactor;
				material.flakesColorStrength = c.flakesColorStrength;
				material.flakesScale = c.flakesScale;
				material.flakeStrength = c.flakeStrength;
				material.fresnelPower = c.fresnelPower;
			})
			.easing(Easing.Quadratic.InOut);
		this._tweenManager.start(colourTween);
	}
}

//-----------------------------------------------------------------------------------------
// Public Functions:
//-----------------------------------------------------------------------------------------

/**
 * Returns a material representing the currently selected body colour, for the other configurators to put on the
 * body-coloured parts they own (the splitter, a body-matching dashboard face). Always a fresh clone, so the caller may
 * modify it - setting a matte smoothness, say - without disturbing the shared paint description material.
 * Returns null if no material can be resolved, having logged why.
 */
export function cloneBodyPaintMaterial(paintDescriptions: PaintDescription[]): CarPbrMaterial | null {
	return clonePaintMaterial(paintDescriptions, Configuration.bodyPaintColour, Configuration.bespokeBodyPaintColour);
}

/**
 * Returns a material representing the given paint selection - an option id and, for a bespoke id, the colour chosen for it - so
 * that either region's paint (the body's or the wings') can be resolved the same way. Always a fresh clone, as for
 * cloneBodyPaintMaterial(). Returns null if no material can be resolved, having logged why.
 */
export function clonePaintMaterial(paintDescriptions: PaintDescription[], id: string, bespokeColour: NumberArray | null): CarPbrMaterial | null {

	// a bespoke colour has no paint description of its own, so its finish is taken from its paint type's base paint
	// and its colour from the one given - the same pair selectBespokeBodyPaint() and selectBespokeWingPaint() apply to the car itself.
	const isBespokeColour = Configuration.isBespokePaintColour(id);
	const materialId = isBespokeColour ? getBespokeBasePaintId(CloudData.getPaintTypeForId(id)) : id;
	if (!materialId) {
		return null;
	}

	// get the paint description supplying the material.
	const paintDescription = paintDescriptions.find(element => element.paintId === materialId);
	if (!paintDescription) {
		console.error('No paint description found for id: ', materialId);
		return null;
	}

	// clone it, so that the caller owns the material it is given.
	const material = paintDescription.paintMaterial?.clone() as CarPbrMaterial;
	if (!material) {
		console.error('No paint material assigned for id: ', materialId);
		return null;
	}

	// for a bespoke colour, overwrite the base material's albedo with the chosen colour, leaving the rest of its finish alone.
	if (isBespokeColour && bespokeColour) {
		material.albedoColor = [bespokeColour[0], bespokeColour[1], bespokeColour[2], bespokeColour[3] ?? 1];
	}

	return material;
}

//-----------------------------------------------------------------------------------------
// Private Functions:
//-----------------------------------------------------------------------------------------

// resolves the id of the neutral base paint a bespoke colour of the given paint type is applied over. a paint type absent from the
// map does not offer the bespoke option at all, so nothing should ever ask for one - hence the log rather than a silent null.
function getBespokeBasePaintId(paintType: PaintTypes): string | null {

	const basePaintId = BESPOKE_BASE_PAINT_IDS[paintType];
	if (!basePaintId) {
		console.error('No bespoke base paint defined for paint type: ', PaintTypes[paintType]);
		return null;
	}

	return basePaintId;
}

// compares two body mesh sets. identity and order are enough: _getBodyMeshes() builds its list the same way every time, so the only
// thing that varies between two calls is whether the conditional mesh at the end of it is present.
function isSameMeshSet(first: MeshComponent[] | null, second: MeshComponent[]): boolean {

	if (!first || first.length !== second.length) {
		return false;
	}

	return first.every((mesh, index) => mesh === second[index]);
}
